import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { catalog as productData } from '../api/governedCatalog';
import { getCurrentUser } from '../../features/auth/api/authUtils';
import { patchAccountProfile, uploadProfilePicture } from '../api/governedCatalog';
import { logger } from '../lib/logger';
import { fetchUserOrgId, useUserOrgId } from '../session/useUserOrgId';
import { fetchCurrentUserAccess } from '../session/useCurrentUserRole';

/**
 * Current user's user_profiles id and org_id (no admin_accounts).
 */
export const fetchCurrentUserProfileBasics = async () => {
  const access = await fetchCurrentUserAccess();
  if (!access?.profileId) {
    throw new Error('User profile not found.');
  }
  if (!access.organizationId) {
    throw new Error('User profile is not linked to an organization.');
  }
  return { userProfileId: access.profileId, orgId: access.organizationId };
};

// --- Admin Account (stored on user_profiles; admin_accounts table removed) ---

/**
 * Fetches profile fields for the current user (facility, contact, etc.).
 * `adminId` is kept as an alias of user_profiles.id for existing call sites.
 */
const fetchAdminAccount = async ({ client } = {}) => {
  const access = await fetchCurrentUserAccess({ client });
  const user = await getCurrentUser();
  if (!user) throw new Error('No authenticated user');
  if (!access?.profileId || !access?.organizationId) {
    throw new Error('Authoritative user profile context is incomplete.');
  }

  const appMetadata = user.app_metadata || {};
  const { data: profile, error } = await productData
    .from('user_profiles')
    .select('first_name, last_name, phone, job_title, profile_picture_path, facility_id')
    .eq('id', access.profileId)
    .maybeSingle();

  if (error) throw error;

  return {
    adminId: access.profileId,
    orgId: access.organizationId,
    email: user.email || null,
    firstName: profile?.first_name || appMetadata.first_name || null,
    lastName: profile?.last_name || appMetadata.last_name || null,
    phoneNumber: profile?.phone || null,
    jobTitle: profile?.job_title || null,
    profilePicturePath: profile?.profile_picture_path || null,
    userProfileId: access.profileId,
    facilityId: profile?.facility_id || access.facilityId || null,
    organizationContext: null,
    facilityContext: null,
  };
};

export const useAdminAccount = ({ enabled = true } = {}) => {
  return useQuery({
    enabled,
    queryKey: ['adminAccount'],
    queryFn: fetchAdminAccount,
    staleTime: 15 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    retry: 1,
    onError: (error) => logger.error('useAdminAccount query failed:', { error }),
  });
};

// --- Operator (Tablet) Accounts ---

/**
 * Fetches operator accounts (tablets) associated with an admin account.
 */
const fetchOperatorAccounts = async (adminAccountId) => {
  if (!adminAccountId) {
    logger.warn('fetchOperatorAccounts called without adminAccountId');
    return [];
  }
  logger.info('Fetching operator accounts for admin ID:', adminAccountId);
  const { data, error } = await productData
    .from('tablets')
    .select('id, created_at, facility_id, user_profile_id, admin_account_id')
    .eq('admin_account_id', adminAccountId);

  if (error) {
    logger.error('Error fetching operator accounts:', { error, adminAccountId });
    throw error;
  }

  const profileIds = [...new Set((data || []).map((row) => row.user_profile_id).filter(Boolean))];
  let profilesById = {};
  if (profileIds.length) {
    const { data: profiles, error: profileError } = await productData
      .from('user_profiles')
      .select('id, email, is_active')
      .in('id', profileIds);
    if (profileError) throw profileError;
    profilesById = Object.fromEntries((profiles || []).map((row) => [row.id, row]));
  }

  logger.info(`Successfully fetched ${data?.length || 0} operator accounts for admin ID:`, adminAccountId);
  return (data || []).map((tablet) => {
    const profile = profilesById[tablet.user_profile_id] || {};
    return {
      id: tablet.id,
      createdAt: tablet.created_at,
      facilityId: tablet.facility_id,
      userProfileId: profile.id || tablet.user_profile_id,
      email: profile.email,
      isActive: profile.is_active,
    };
  });
};

export const useOperatorAccounts = (adminAccountId) => {
  return useQuery({
    queryKey: ['operatorAccounts', adminAccountId],
    queryFn: () => fetchOperatorAccounts(adminAccountId),
    enabled: !!adminAccountId,
    staleTime: 5 * 60 * 1000,
    onError: (error) => logger.error('useOperatorAccounts query failed:', { error, adminAccountId }),
  });
};

// --- Tablet Credentials ( Potentially less used, specific use case) ---
// Kept separate as it fetches credentials, maybe only needed in specific components.
// Note: There is no relationship between tablets and production lines.

const fetchTabletCredentials = async (tabletId) => {
    if (!tabletId) return null;

    const { data: tablet, error } = await productData
        .from('tablets')
        .select('id, user_profile_id')
        .eq('id', tabletId)
        .single();

    if (error) {
        logger.error("Error fetching tablet credentials", { tabletId, error });
        throw error;
    }

    if (!tablet?.user_profile_id) return null;
    const { data: profile, error: profileError } = await productData
        .from('user_profiles')
        .select('email')
        .eq('id', tablet.user_profile_id)
        .maybeSingle();
    if (profileError) throw profileError;

    return {
        id: tablet.id,
        email: profile?.email || null,
    };
};


export const useTabletCredentialsQuery = (tabletId) => {
    return useQuery({
        queryKey: ['tabletCredentials', tabletId],
        queryFn: () => fetchTabletCredentials(tabletId),
        enabled: !!tabletId,
        staleTime: 5 * 60 * 1000,
    });
};


// --- Current user role (canonical implementation in shared/session) ---

// --- Organization Details ---

/**
 * Fetches the organization details for the currently authenticated user.
 */
const fetchOrganizationDetails = async () => {
  const orgId = await fetchUserOrgId();

  const { data: organization, error } = await productData
    .from('organizations')
    .select('id, name, industry')
    .eq('id', orgId)
    .single();

  if (error) {
    logger.error('Error fetching organization details:', { orgId, error });
    throw new Error(`Failed to fetch organization details: ${error.message}`);
  }

  return organization;
};

export const useOrganizationDetails = () => {
  return useQuery({
    queryKey: ['organizationDetails'],
    queryFn: fetchOrganizationDetails,
    staleTime: 15 * 60 * 1000, // Cache for 15 mins
  });
};

// --- Admin Profile Update ---

const USER_PROFILE_UPDATE_COLUMNS = new Set([
  'first_name',
  'last_name',
  'email',
  'job_title',
  'phone',
  'profile_picture_path',
  'facility_id',
]);

/** Map legacy admin_accounts field names to user_profiles columns. */
const normalizeUserProfileUpdates = (updates) => {
  const alias = { phone_number: 'phone' };
  const out = {};
  for (const [key, value] of Object.entries(updates || {})) {
    const col = alias[key] || key;
    if (USER_PROFILE_UPDATE_COLUMNS.has(col)) {
      out[col] = value;
    }
  }
  return out;
};

/**
 * Updates admin profile information on user_profiles.
 */
const updateAdminProfile = async ({ adminId, updates }) => {
  if (!adminId) {
    throw new Error('Admin ID is required');
  }

  const normalized = normalizeUserProfileUpdates(updates);
  if (Object.keys(normalized).length === 0) {
    return [];
  }

  const result = await patchAccountProfile({
    profile_id: adminId,
    profile: normalized,
  });
  return result.profile || [];
};

/**
 * Persists lean org/facility profile fields on organizations / facilities.
 */
const updateOrgFacilityContext = async ({ orgId, facilityId, organization, facility }) => {
  if (!orgId) {
    throw new Error('Organization ID is required');
  }
  await patchAccountProfile({
    organization,
    facility_id: facilityId,
    facility,
  });
};

export const useUpdateOrgFacilityContextMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateOrgFacilityContext,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminAccount'] });
    },
    onError: (error) => {
      logger.error('Org/facility context update failed:', { error });
    },
  });
};

export const useUpdateAdminProfileMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ adminId, updates }) => updateAdminProfile({ adminId, updates }),
    onSuccess: (data, { adminId }) => {
      logger.info('Admin profile updated successfully:', { adminId });
      // Invalidate the admin account query to refetch
      queryClient.invalidateQueries({ queryKey: ['adminAccount'] });
    },
    onError: (error, { adminId }) => {
      logger.error('Admin profile update mutation failed:', { adminId, error });
    },
  });
};

// --- Profile Picture Upload ---

/**
 * Uploads a profile picture to the storage bucket and updates the admin account
 */
const uploadAdminProfilePicture = async ({ adminId, file }) => {
  if (!adminId || !file) {
    throw new Error('Admin ID and file are required');
  }
  const result = await uploadProfilePicture({ file, profileId: adminId });
  return {
    uploadData: result,
    updateData: result,
    filePath: result.path,
  };
};

export const useUploadProfilePictureMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ adminId, file }) => uploadAdminProfilePicture({ adminId, file }),
    onSuccess: (data, { adminId }) => {
      logger.info('Profile picture uploaded successfully:', { adminId, filePath: data.filePath });
      // Invalidate the admin account query to refetch
      queryClient.invalidateQueries({ queryKey: ['adminAccount'] });
    },
    onError: (error, { adminId }) => {
      logger.error('Profile picture upload mutation failed:', { adminId, error });
    },
  });
};

export { useUserOrgId, fetchUserOrgId } from '../session/useUserOrgId';
export { useCurrentUserRole, fetchCurrentUserRole } from '../session/useCurrentUserRole';
